import { useRef, useState } from "react";

import { validateDateRange } from "shared/lib/date/validateDateRange";
import { alertDialog } from "shared/ui/feedback";

/**
 * SearchPanel이 선언한 필드(fields)로부터 기본값을 계산한다.
 * - condition: 조건검색 드롭다운의 첫 옵션(없으면 null)
 * - chips: 각 칩 행의 기본 선택값(deselectTo, 없으면 null)
 * - quickRangeLength: 빠른 기간 칩 개수(boolean 배열 길이 산정용)
 */
const computeDefaults = (fields) => {
  const keywordField = fields.find((f) => f.kind === "keyword");
  const chipsFields = fields.filter((f) => f.kind === "chips");
  const quickRangeField = fields.find((f) => f.kind === "quickRange");

  return {
    condition: keywordField?.conditionOptions?.[0] ?? null,
    chips: Object.fromEntries(
      chipsFields.map((f) => [f.name, f.deselectTo ?? null]),
    ),
    quickRangeLength: quickRangeField?.options?.length ?? 0,
    // 빠른 기간 칩에 딸린 선택 드롭다운(예: 가입일/최근 로그인)의 기본값
    rangeField: quickRangeField?.dropdown?.[0] ?? null,
  };
};

// 선택된 빠른 기간 index → applyDateFilter가 쓰는 boolean 배열로 변환.
const toBoolArray = (selectedIndex, length) => {
  const arr = Array(length).fill(false);
  if (selectedIndex !== null && selectedIndex >= 0 && selectedIndex < length) {
    arr[selectedIndex] = true;
  }
  return arr;
};

/**
 * 조건 검색 패널의 폼 상태/규칙을 한 곳에 모은 훅.
 *
 * - 빠른 기간 칩 ↔ 직접 입력 범위의 상호 배타
 * - 날짜 범위 유효성 검증(validateDateRange)
 * - "검색 활성" 여부 일반화(기본값과 다른 필드가 하나라도 있으면 true)
 * - 정규화된 filters 객체로 출력(submit)
 */
export function useSearchFilters(fields) {
  // fields는 선언적 정적 구성이므로 기본값은 최초 1회만 계산한다.
  const defaultsRef = useRef(null);
  if (!defaultsRef.current) defaultsRef.current = computeDefaults(fields);
  const defaults = defaultsRef.current;

  const [keyword, setKeyword] = useState("");
  const [condition, setCondition] = useState(defaults.condition);
  const [chips, setChips] = useState(defaults.chips);
  const [quickRange, setQuickRangeState] = useState(null);
  const [rangeField, setRangeField] = useState(defaults.rangeField);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const setChip = (name, value) =>
    setChips((prev) => ({ ...prev, [name]: value }));

  // 빠른 기간 선택 시 직접 입력 범위는 비운다(상호 배타).
  const setQuickRange = (next) => {
    if (next === null) {
      setQuickRangeState(null);
      return;
    }
    setQuickRangeState(next);
    setStartDate("");
    setEndDate("");
  };

  // 직접 범위 입력 시 빠른 기간 선택은 해제(상호 배타).
  const onChangeStart = (event) => {
    const next = event.target.value;
    const validation = validateDateRange(next, endDate);
    if (!validation.isValid) {
      alertDialog(validation.message);
      setStartDate(next);
      setEndDate("");
      setQuickRangeState(null);
      return;
    }
    setStartDate(next);
    setQuickRangeState(null);
  };

  const onChangeEnd = (event) => {
    const next = event.target.value;
    const validation = validateDateRange(startDate, next);
    if (!validation.isValid) {
      alertDialog(validation.message);
      setEndDate("");
      setQuickRangeState(null);
      return;
    }
    setEndDate(next);
    setQuickRangeState(null);
  };

  const chipsChanged = Object.keys(defaults.chips).some(
    (name) => chips[name] !== defaults.chips[name],
  );
  const searchActive =
    keyword !== "" ||
    quickRange !== null ||
    Boolean(startDate && endDate) ||
    chipsChanged;

  const buildFilters = () => ({
    keyword,
    condition,
    chips,
    quickRange,
    rangeField,
    dateButtonClicked: toBoolArray(quickRange, defaults.quickRangeLength),
    startDate,
    endDate,
  });

  const submit = (onSearch) => {
    const validation = validateDateRange(startDate, endDate);
    if (!validation.isValid) {
      alertDialog(validation.message);
      return;
    }
    onSearch?.(buildFilters());
  };

  const reset = (onReset) => {
    setKeyword("");
    setCondition(defaults.condition);
    setChips(defaults.chips);
    setQuickRangeState(null);
    setRangeField(defaults.rangeField);
    setStartDate("");
    setEndDate("");
    onReset?.();
  };

  return {
    keyword,
    setKeyword,
    condition,
    setCondition,
    chips,
    setChip,
    quickRange,
    setQuickRange,
    rangeField,
    setRangeField,
    startDate,
    endDate,
    onChangeStart,
    onChangeEnd,
    searchActive,
    submit,
    reset,
  };
}

export default useSearchFilters;
